import axios from 'axios';
import { MutationTree, ActionTree, GetterTree } from 'vuex';
import { RootState } from './index.d';
import {
  TicketsState,
  TicketInformation,
  TicketReplyInformation,
} from './ticketsStore.d';
import config from '../config';

const { apiRoutes } = config;

const ticketsState: TicketsState = {
  tickets: null,
  ticketsLoadingState: 'idle',
  ticket: null,
  ticketLoadingState: 'idle',
  search: null,
  ticketTypes: null,
  ticketsPage: 1,
  statusUpdateLoadingState: 'idle',
  createTicketLoadingState: 'idle',
  ticketReplyLoadingState: 'idle',
};

const mutations: MutationTree<TicketsState> = {
  setTickets(state, tickets) {
    state.tickets = tickets;
  },

  setTicket(state, ticket) {
    state.ticket = ticket;
  },

  setTicketsSearch(state, searchValue) {
    state.search = searchValue !== '' ? searchValue : null;
  },

  setTicketsPage(state, page: number) {
    state.ticketsPage = page;
  },

  setTicketTypes(state, ticketTypes) {
    state.ticketTypes = ticketTypes;
  },

  setTicketsLoadingState(state, loadingState) {
    state.ticketsLoadingState = loadingState;
  },

  setTicketLoadingState(state, loadingState) {
    state.ticketLoadingState = loadingState;
  },

  setStatusLoadingState(state, loadingState) {
    state.statusUpdateLoadingState = loadingState;
  },

  setCreateTicketLoadingState(state, loadingState) {
    state.createTicketLoadingState = loadingState;
  },

  setTicketReplyLoadingState(state, loadingState) {
    state.ticketReplyLoadingState = loadingState;
  },
};

const actions: ActionTree<TicketsState, RootState> = {
  fetchTickets({ commit, state }) {
    commit('setTicketsLoadingState', 'loading');
    commit('setTickets', null);

    let ticketsApi = apiRoutes.tickets;

    ticketsApi += `?page=${state.ticketsPage}`;

    if (state.search) {
      ticketsApi += `&search=${state.search}`;
    }

    return axios
      .get(ticketsApi)
      .then(({ data }) => {
        commit('setTickets', data);
        commit('setTicketsLoadingState', 'loaded');
      })
      .catch((err) => {
        commit('setTicketsLoadingState', 'error');
        throw err;
      });
  },

  fetchTicket({ commit }, id) {
    commit('setTicketLoadingState', 'loading');
    commit('setTicket', null);

    const apiUrl = apiRoutes.ticket.replace('{id}', id);

    return axios
      .get(apiUrl)
      .then(({ data }) => {
        commit('setTicket', data);
        commit('setTicketLoadingState', 'loaded');
      })
      .catch((err) => {
        commit('setTicketLoadingState', 'error');
        throw err;
      });
  },

  closeTicket({ dispatch }, id) {
    return dispatch('toggleTicketState', [id, 'closed']);
  },

  openTicket({ dispatch }, id) {
    return dispatch('toggleTicketState', [id, 'open']);
  },

  async toggleTicketState({ dispatch }, [id, status]) {
    this.commit('setStatusLoadingState', 'loading');

    const apiUrl = apiRoutes.ticketState.replace('{id}', id);

    try {
      await axios.post(apiUrl, {
        status,
      });

      this.commit('setStatusLoadingState', 'loaded');
    } catch (err) {
      this.commit('setStatusLoadingState', 'error');
    }

    return dispatch('fetchTicket', id);
  },

  createTicket({ commit }, ticketInformation: TicketInformation) {
    commit('setCreateTicketLoadingState', 'loading');

    return axios
      .post(apiRoutes.ticketCreate, ticketInformation)
      .then((res) => {
        commit('setCreateTicketLoadingState', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('setCreateTicketLoadingState', 'error');
        throw err;
      });
  },

  createTicketReply({ commit }, replyInformation: TicketReplyInformation) {
    commit('setTicketReplyLoadingState', 'loading');

    const apiUrl = apiRoutes.ticketReplyCreate.replace('{id}', replyInformation.ticketId.toString());

    return axios
      .post(apiUrl, replyInformation)
      .then((res) => {
        commit('setTicketReplyLoadingState', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('setTicketReplyLoadingState', 'error');
        throw err;
      });
  },

  searchTickets({ commit, dispatch }, searchValue) {
    commit('setTicketsPage', 1);
    commit('setTicketsSearch', searchValue);

    dispatch('fetchTickets');
  },

  fetchTicketTypes({ commit }) {
    return axios.get(apiRoutes.ticketTypes)
      .then((res) => {
        commit('setTicketTypes', res.data);
        return res;
      });
  },
};

const getters: GetterTree<TicketsState, RootState> = {
  tickets(state) {
    return state.tickets;
  },

  ticket(state) {
    return state.ticket;
  },

  ticketTypes(state) {
    return state.ticketTypes;
  },

  ticketsLoadingState(state) {
    return state.ticketsLoadingState;
  },

  ticketLoadingState(state) {
    return state.ticketLoadingState;
  },

  statusUpdateLoadingState(state) {
    return state.statusUpdateLoadingState;
  },

  createTicketLoadingState(state) {
    return state.createTicketLoadingState;
  },

  ticketReplyLoadingState(state) {
    return state.ticketReplyLoadingState;
  },
};

export default {
  state: ticketsState,
  getters,
  mutations,
  actions,
};
